/**
 * Module dependencies.
 */

import { Avatar } from 'app/components/ui/avatar';
import { cookieStorage } from 'app/core/utils/cookie-storage';
import React from 'react';

/**
 * Brand palette (shared by the landing and sign-in pages).
 */

export const GREEN_DARK = '#1B752B';
export const GREEN_MID = '#22A544';
export const GREEN_BRIGHT = '#34C759';

/**
 * Authenticated user (name / email / initials) read from the cookie, or null.
 */

export type AuthUser = { email: string; initials: string; name: string; photo: string | null };

export function getAuthUser(): AuthUser | null {
  const user = cookieStorage.get('user') as
    | { email?: string; firstName?: string; lastName?: string; name?: string; profilePhotoUrl?: string }
    | undefined;

  if (!user) {
    return null;
  }

  const name = user.name || [user.firstName, user.lastName].filter(Boolean).join(' ') || 'User';
  const email = user.email ?? '';
  const initials = (name || email || 'U')
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('');

  return { email, initials, name, photo: user.profilePhotoUrl ?? null };
}

/**
 * Brand logo — open "C" ring + dot, followed by the "ontab" wordmark.
 */

export function Logo({ size = 39 }: { size?: number }): JSX.Element {
  return (
    <div className={"flex items-center font-['Outfit'] font-semibold tracking-[-0.03em]"}>
      <svg fill={'none'} height={size} viewBox={'0 0 24 24'} width={size} xmlns={'http://www.w3.org/2000/svg'}>
        <path d={'M17.5 7.2a7 7 0 1 0 0 9.6'} stroke={GREEN_DARK} strokeLinecap={'round'} strokeWidth={2.6} />

        <circle cx={'17.6'} cy={'12'} fill={GREEN_MID} r={'1.7'} />
      </svg>

      <span className={'leading-none'} style={{ color: GREEN_DARK, fontSize: size * 0.56, marginLeft: size * -0.1 }}>
        {'ontab'}
      </span>
    </div>
  );
}

/**
 * User badge — avatar initials + name + email. Used in the landing nav when signed in.
 */

export function UserBadge({ onClick, user }: { onClick: () => void; user: AuthUser }): JSX.Element {
  return (
    <button
      className={'flex items-center gap-2.5 transition-opacity hover:opacity-80'}
      onClick={onClick}
      type={'button'}
    >
      <Avatar className={'h-9 w-9 text-xs'} initials={user.initials} src={user.photo} />

      <div className={'text-left leading-tight'}>
        <div className={'text-[13px] font-semibold text-neutral-800'}>{user.name}</div>

        {user.email && <div className={'text-[11px] text-neutral-500'}>{user.email}</div>}
      </div>
    </button>
  );
}
